import { last } from 'lodash';

import { SemboDate } from '@libs/sembo-dates';
import type {
  BoardBasisCode,
  ChronologicalItinerary,
  DecimalRangeType,
  HeartedHotel,
  HotelBookableOptionRoom,
} from '@libs/trip-planner-interfaces';
import { getLongDatePeriod, isDefined } from '@libs/utils';

interface HeartedRoomMatcher {
  code: string;
  boardBasis?: BoardBasisCode;
  roomTypeId?: number;
}

export function getTravelDateRange(
  chronologicalItinerary: ChronologicalItinerary[]
): string {
  const firstDayTrip = chronologicalItinerary[0];
  const lastDayTrip = last(chronologicalItinerary);

  if (firstDayTrip && lastDayTrip) {
    const endDate = new SemboDate(lastDayTrip.startDate)
      .add({ days: lastDayTrip.numberOfNights })
      .get();
    return getLongDatePeriod(firstDayTrip.startDate, endDate);
  }

  return '';
}

export function getHeartedRoomMatchers(
  heartedHotel: HeartedHotel
): HeartedRoomMatcher[] | undefined {
  return (
    heartedHotel.rooms ?? heartedHotel.roomCodes?.map((code) => ({ code }))
  );
}

export function stayRoomMatchesHeartedRoom(
  stayRoom: HotelBookableOptionRoom,
  heartedRoom: HeartedRoomMatcher
): boolean {
  if (heartedRoom.roomTypeId !== undefined) {
    return (
      stayRoom.roomTypeId === heartedRoom.roomTypeId &&
      stayRoom.boardBasis === heartedRoom.boardBasis
    );
  }

  if (stayRoom.roomCode !== heartedRoom.code) {
    return false;
  }

  return (
    heartedRoom.boardBasis === undefined ||
    stayRoom.boardBasis === heartedRoom.boardBasis
  );
}

export function getWidestRange(
  ranges: (DecimalRangeType | undefined)[]
): Required<DecimalRangeType> | undefined {
  const fromValues = ranges.map((range) => range?.from).filter(isDefined);
  const toValues = ranges.map((range) => range?.to).filter(isDefined);

  if (!fromValues.length || !toValues.length) {
    return undefined;
  }

  return {
    from: Math.min(...fromValues),
    to: Math.max(...toValues),
  };
}

/**
 * Carrier memory is only valid while the leg still searches the same route.
 */
export function getTransportRouteKey(ingredient: {
  fromPort?: string;
  toPort?: string;
}): string {
  return `${ingredient.fromPort ?? ''}-${ingredient.toPort ?? ''}`;
}
